/**
 * The one page structure every console screen is assembled from.
 *
 * Before this, each screen built its own header, description, filter strip and
 * actions, and a page-level verb like "Create account" lived as a permanently
 * expanded form pushed below the table it created rows in - unreachable by
 * keyboard without tabbing the whole table first, and easy to miss entirely.
 * The shell fixes the positions instead of the styling: title and purpose top
 * left, page verbs top right, one toolbar for search and filters, then the
 * content. Screens keep composing their own content from `Section`, `StatTile`
 * and the rest; what they no longer decide is where any of it goes.
 *
 * `ActionDialog` is the other half of the same fix. A creation form belongs
 * behind a header verb, and a dialog is only an improvement over an inline
 * form if it actually behaves like one: focus moves into it, Tab stays inside
 * it, Escape closes it, and focus returns to the button that opened it.
 */

import { useCallback, useEffect, useId, useRef, type FormEvent, type ReactNode } from 'react';
import { SearchLg, XClose } from '@untitledui/icons';
import { Button } from '../components/base/buttons/button';
import { cx } from '../utils/cx';

/**
 * Title, one-sentence purpose, and the page's own verbs, always in that
 * arrangement. `actions` is the only place a page-level verb belongs.
 */
export function ConsolePage({
  title,
  purpose,
  actions,
  toolbar,
  band,
  children,
}: {
  title: string;
  /** One sentence on what this page is for. Every screen has one; this is where it goes. */
  purpose?: string;
  /** Page-level verbs - "Create account", "Refresh". Rendered before the content in the DOM. */
  actions?: ReactNode;
  /**
   * What narrows this page. The shell composes it - one search box, then the
   * screen's own filters, then refresh - so two screens doing the same job
   * cannot arrange the same controls differently. A screen chooses which parts
   * it has, never their order or their spelling.
   */
  toolbar?: PageFilters;
  /**
   * The screen's summary figures, as a `MetricBand`. The shell fixes where a
   * band goes - directly under the toolbar, above the content - so a screen
   * chooses its figures and never its position.
   */
  band?: ReactNode;
  children: ReactNode;
}) {
  return (
    <main
      className="mx-auto flex w-full max-w-[1600px] min-w-0 flex-col gap-5 px-4 py-6 md:px-8"
      id="main-content"
    >
      <header className="flex flex-col gap-4 border-b border-secondary pb-5">
        <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          <div className="flex min-w-0 flex-col gap-1">
            <h1 className="text-xl font-semibold text-primary">{title}</h1>
            {purpose ? <p className="max-w-3xl text-sm text-tertiary">{purpose}</p> : null}
          </div>
          {actions ? (
            // Immediately after the title in the DOM, so a keyboard reaches a
            // page's own verbs before its table rather than after it.
            <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>
          ) : null}
        </div>
      </header>
      {toolbar ? <PageToolbar {...toolbar} /> : null}
      {band}
      {children}
    </main>
  );
}

/**
 * The one place search, filters and refresh live. It is a `search` landmark
 * because that is what it is, and it is above the content on every screen so
 * an operator never hunts for where a page is narrowed.
 */
export type PageFilters = {
  /** The one search box, when a screen has something to search by. */
  search?: {
    initial: string;
    placeholder: string;
    label?: string;
    onSearch: (value: string) => void;
  };
  /** The screen's own narrowing controls - selects, date ranges. */
  filters?: ReactNode;
  /** Put every filter back to its default. Rendered before refresh. */
  onClear?: () => void;
  /** False when nothing is narrowed, so clearing would do nothing. */
  canClear?: boolean;
  /** Re-read this page. The shell renders the button, so it reads the same everywhere. */
  onRefresh?: () => void;
  /** True while that re-read is in flight. */
  refreshing?: boolean;
};

export function PageToolbar({
  search,
  filters,
  onClear,
  canClear,
  onRefresh,
  refreshing,
}: PageFilters) {
  return (
    <div
      role="search"
      aria-label="Filter this page"
      className="flex flex-wrap items-end gap-3 rounded-xl border border-secondary bg-primary p-3"
    >
      {search ? <SearchField {...search} /> : null}
      {filters}
      {onClear || onRefresh ? (
        <ToolbarActions>
          {onClear ? (
            <Button size="md" color="secondary" isDisabled={canClear === false} onClick={onClear}>
              Clear filters
            </Button>
          ) : null}
          {onRefresh ? (
            <Button size="md" color="secondary" isDisabled={refreshing} onClick={onRefresh}>
              {refreshing ? 'Refreshing…' : 'Refresh'}
            </Button>
          ) : null}
        </ToolbarActions>
      ) : null}
    </div>
  );
}

/** A submit-on-enter search box. `label` names it, so two on one page cannot collide. */
export function SearchField({
  label = 'Search',
  initial,
  placeholder,
  onSearch,
}: {
  label?: string;
  initial: string;
  placeholder: string;
  onSearch: (value: string) => void;
}) {
  const id = useId();
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = new FormData(event.currentTarget).get('query');
    onSearch(typeof value === 'string' ? value.trim() : '');
  };
  return (
    <form className="flex min-w-64 flex-1 flex-wrap items-end gap-2" onSubmit={submit}>
      <div className="flex min-w-0 flex-1 flex-col gap-1.5">
        {/* The placeholder says what to type, the icon says what the field is,
            and the button beside it says the verb. A visible label made that a
            third statement of the same word and pushed the field onto two rows
            while the button stayed on one. */}
        <label htmlFor={id} className="sr-only">
          {label}
        </label>
        <div className="relative flex items-center">
          <SearchLg className="pointer-events-none absolute left-3 size-4 text-fg-quaternary" />
          <input
            id={id}
            name="query"
            type="search"
            defaultValue={initial}
            placeholder={placeholder}
            className="w-full rounded-lg border border-primary bg-primary py-2 pr-3 pl-9 text-md text-primary shadow-xs outline-brand placeholder:text-placeholder focus:outline-2"
          />
        </div>
      </div>
      <Button type="submit" size="md" color="secondary">
        {label}
      </Button>
    </form>
  );
}

/** The trailing group in a toolbar - refresh, reset - kept out of the filter flow. */
export function ToolbarActions({ children }: { children: ReactNode }) {
  return <div className="flex flex-wrap items-end gap-2">{children}</div>;
}

const FOCUSABLE =
  'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

/**
 * A modal that behaves like one.
 *
 * The complaint that started this was that a creation form was inaccessible,
 * so the three things that make a dialog reachable are not optional here:
 * focus enters the dialog on open, Tab cycles within it rather than escaping
 * to the page behind, and on close focus returns to whatever had it before -
 * which is the header button the operator pressed.
 */
export function ActionDialog({
  title,
  description,
  onClose,
  children,
  footer,
  width = 'md',
}: {
  title: string;
  description?: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  width?: 'md' | 'lg';
}) {
  const titleId = useId();
  const descriptionId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  const restoreTo = useRef<HTMLElement | null>(null);

  const focusables = useCallback(
    () => Array.from(dialogRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? []),
    [],
  );

  useEffect(() => {
    restoreTo.current = document.activeElement as HTMLElement | null;
    const first = focusables()[0];
    (first ?? dialogRef.current)?.focus();
    return () => {
      // Returning focus is what makes the dialog a detour rather than a
      // dead end: the operator lands back on the button they pressed.
      restoreTo.current?.focus?.();
    };
  }, [focusables]);

  const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      event.stopPropagation();
      onClose();
      return;
    }
    if (event.key !== 'Tab') return;
    const items = focusables();
    if (!items.length) return;
    const first = items[0] as HTMLElement;
    const last = items[items.length - 1] as HTMLElement;
    const active = document.activeElement;
    if (event.shiftKey && (active === first || active === dialogRef.current)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-overlay/70 p-4 backdrop-blur-sm"
      onMouseDown={onClose}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descriptionId : undefined}
        tabIndex={-1}
        onMouseDown={(event) => event.stopPropagation()}
        onKeyDown={onKeyDown}
        className={cx(
          'my-8 flex w-full flex-col gap-4 rounded-xl border border-secondary bg-primary p-5 shadow-lg outline-hidden',
          width === 'lg' ? 'max-w-4xl' : 'max-w-2xl',
        )}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 flex-col gap-1">
            <h2 id={titleId} className="text-md font-semibold text-primary">
              {title}
            </h2>
            {description ? (
              <p id={descriptionId} className="text-sm text-tertiary">
                {description}
              </p>
            ) : null}
          </div>
          <Button
            size="sm"
            color="tertiary"
            iconLeading={XClose}
            aria-label={`Close ${title}`}
            onClick={onClose}
          />
        </div>
        <div className="flex min-w-0 flex-col gap-4">{children}</div>
        {footer ? (
          <div className="flex flex-wrap items-center justify-end gap-2 border-t border-secondary pt-4">
            {footer}
          </div>
        ) : null}
      </div>
    </div>
  );
}
